import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, MessagesSquare, CalendarRange, Send, Loader2, UserRound, ArrowUpRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import { BackLink } from "@/components/PageHeader";
import AccountMenu, { SignupLink } from "@/components/AccountMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Which races are coming up in India?",
  "How do I join Gratitude Miles?",
  "What gear do I need for my first marathon?",
  "Tell me about travelling for a marathon in India",
];

async function streamInto(path, body, onToken) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      if (!p.startsWith("data: ")) continue;
      const data = p.slice(6).trim();
      if (data === "[DONE]") return;
      try {
        onToken(JSON.parse(data).t || "");
      } catch {
        /* partial chunk */
      }
    }
  }
}

const inputCls =
  "w-full bg-[#FFFFFF] border border-[#0D1117]/15 rounded-xl px-4 py-3 text-sm text-[#0D1117] placeholder:text-[#6B7280] focus:border-[#FF4E00]/60 focus:outline-none transition-all duration-300";

function Concierge({ model }) {
  const [sessionId] = useState(() => {
    let s = localStorage.getItem("fg_ai_session");
    if (!s) {
      s = crypto.randomUUID();
      localStorage.setItem("fg_ai_session", s);
    }
    return s;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/ai/history/${sessionId}`)
      .then((r) => r.json())
      .then((m) => setMessages(m))
      .catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);
    try {
      await streamInto("/ai/chat", { session_id: sessionId, message, model }, (t) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { ...copy[copy.length - 1], text: copy[copy.length - 1].text + t };
          return copy;
        });
      });
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], text: "Connection dropped — please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="ai-concierge" className="rounded-3xl border border-[#0D1117]/10 bg-[#FFFFFF] overflow-hidden">
      <div className="max-h-[52vh] overflow-y-auto p-6 sm:p-8 space-y-5">
        {messages.length === 0 && (
          <div className="py-6">
            <p className="font-serif text-xl sm:text-2xl text-[#0D1117] mb-6">
              Namaste, runner. Ask me about races, trips, gear or training.
            </p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  data-testid="ai-suggestion-chip"
                  className="font-mono text-[11px] tracking-[0.06em] border border-[#FF4E00]/40 text-[#FF4E00] hover:bg-[#FF4E00] hover:text-[#FBF9F5] rounded-full px-4 py-2 transition-all duration-300"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid="ai-message">
            <div
              className={`max-w-[85%] rounded-2xl px-5 py-3.5 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-[#FF4E00] text-[#FBF9F5] rounded-br-sm"
                  : "bg-[#F4F2EC] text-[#0D1117] rounded-bl-sm"
              }`}
            >
              {m.text || (busy && i === messages.length - 1 ? "…" : "")}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="border-t border-[#0D1117]/10 p-4 sm:p-5 flex gap-3"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about races, travel, gear, training..."
          className={inputCls}
          data-testid="ai-chat-input"
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          data-testid="ai-chat-send"
          className="shrink-0 w-12 h-12 rounded-full bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-50 text-[#FBF9F5] flex items-center justify-center transition-colors duration-300"
          aria-label="Send message"
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        </button>
      </form>
    </div>
  );
}

function PlanGenerator({ onRequestPersonal, model }) {
  const [form, setForm] = useState({ goal: "Half Marathon", weeks: 8, days_per_week: 4, experience: "beginner", notes: "" });
  const [plan, setPlan] = useState("");
  const [busy, setBusy] = useState(false);

  const generate = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setPlan("");
    try {
      await streamInto("/ai/plan", { ...form, model }, (t) => setPlan((p) => p + t));
    } catch {
      setPlan("Connection dropped — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="ai-plan">
      <form onSubmit={generate} className="lg:col-span-4 rounded-3xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6 sm:p-8 space-y-5 h-fit">
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-2">Goal</label>
          <select value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} className={`${inputCls} appearance-none`} data-testid="ai-plan-goal">
            {["First 5K", "10K", "Half Marathon", "Marathon", "50K Ultra", "General Fitness"].map((g) => (
              <option key={g} value={g} className="bg-[#FFFFFF]">{g}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-2">Weeks</label>
            <input type="number" min="4" max="24" value={form.weeks} onChange={(e) => setForm({ ...form, weeks: Number(e.target.value) })} className={inputCls} data-testid="ai-plan-weeks" />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-2">Days / week</label>
            <input type="number" min="2" max="6" value={form.days_per_week} onChange={(e) => setForm({ ...form, days_per_week: Number(e.target.value) })} className={inputCls} data-testid="ai-plan-days" />
          </div>
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-2">Experience</label>
          <select value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} className={`${inputCls} appearance-none`} data-testid="ai-plan-experience">
            {["beginner", "intermediate", "advanced"].map((g) => (
              <option key={g} value={g} className="bg-[#FFFFFF]">{g}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-2">Notes (optional)</label>
          <textarea rows={3} placeholder="e.g. coming back from a knee niggle..." value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} resize-none`} data-testid="ai-plan-notes" />
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="ai-plan-generate"
          className="w-full inline-flex items-center justify-center gap-3 bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-60 text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.25em] px-8 py-4 rounded-full transition-all duration-300"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {busy ? "Coaching..." : "Generate My Plan"}
        </button>
      </form>
      <div className="lg:col-span-8 rounded-3xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6 sm:p-8 min-h-[300px]">
        {plan ? (
          <>
            <div className="text-sm text-[#0D1117] leading-relaxed whitespace-pre-wrap" data-testid="ai-plan-output">{plan}</div>
            {!busy && (
              <div className="mt-8 pt-6 border-t border-[#0D1117]/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-sm text-[#374151]">Want a coach to tailor this around your life, injuries and race calendar?</p>
                <button
                  onClick={onRequestPersonal}
                  data-testid="ai-plan-personal-cta"
                  className="shrink-0 inline-flex items-center gap-2 border border-[#FF4E00]/50 hover:bg-[#FF4E00] hover:text-[#FBF9F5] text-[#FF4E00] font-mono text-[11px] uppercase tracking-[0.2em] px-6 py-3 rounded-full transition-all duration-300"
                >
                  Get a Personalised Plan <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B7280] pt-2">
            Your week-by-week plan will stream in here — built around your goal, schedule and experience.
          </p>
        )}
      </div>
    </div>
  );
}

function PersonalPlan({ initialGoal }) {
  const [form, setForm] = useState({ name: "", email: "", goal: initialGoal || "Half Marathon", notes: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        email: form.email,
        realm: "Personalised Training Plan (Ask AI)",
        message: `Goal: ${form.goal}${form.notes ? `\n\nNotes: ${form.notes}` : ""}`,
      });
      toast.success("Request received — a Fitygrity coach will reach out within 48 hours.");
      setForm({ name: "", email: "", goal: initialGoal || "Half Marathon", notes: "" });
    } catch {
      toast.error("Could not send your request — please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="ai-personal">
      <div className="lg:col-span-5 rounded-3xl bg-[#0D1117] p-8 sm:p-10">
        <UserRound className="w-7 h-7 text-[#FF4E00] mb-6" />
        <h2 className="font-serif text-2xl sm:text-3xl text-[#FBF9F5] tracking-tight">A plan built around <span className="italic text-[#FF4E00]">you.</span></h2>
        <p className="text-sm text-[#FBF9F5]/70 mt-4 leading-relaxed">
          The AI plan is a starting line. Share your details and a real Fitygrity coach will shape it around
          your schedule, your body and your race goals — then walk the journey with you.
        </p>
        <ul className="mt-6 space-y-3">
          {["Coach-reviewed weekly structure", "Adjusted for travel, work and niggles", "Race-day pacing and fueling strategy"].map((t) => (
            <li key={t} className="flex items-start gap-2.5 text-sm text-[#FBF9F5]/80">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF4E00] mt-1.5 shrink-0" />
              {t}
            </li>
          ))}
        </ul>
      </div>
      <form onSubmit={submit} className="lg:col-span-7 rounded-3xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6 sm:p-8 space-y-5 h-fit">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="ai-personal-name" />
          <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="ai-personal-email" />
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-2">Goal</label>
          <select value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} className={`${inputCls} appearance-none`} data-testid="ai-personal-goal">
            {["First 5K", "10K", "Half Marathon", "Marathon", "50K Ultra", "General Fitness"].map((g) => (
              <option key={g} value={g} className="bg-[#FFFFFF]">{g}</option>
            ))}
          </select>
        </div>
        <textarea rows={4} placeholder="Anything the coach should know — current mileage, injuries, race dates..." value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} resize-none`} data-testid="ai-personal-notes" />
        <button
          type="submit"
          disabled={busy}
          data-testid="ai-personal-submit"
          className="w-full inline-flex items-center justify-center gap-3 bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-60 text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.25em] px-8 py-4 rounded-full transition-all duration-300"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Request My Personalised Plan
        </button>
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#6B7280] text-center">Response within 48 hours · Private, no-spam</p>
      </form>
    </div>
  );
}

export default function AskFitygrity() {
  const [tab, setTab] = useState("concierge");
  const [model, setModel] = useState("gpt");
  const [personalGoal, setPersonalGoal] = useState("");

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <header className="fixed top-0 left-0 w-full z-50 backdrop-blur-xl bg-[#FBF9F5]/75 border-b border-[#0D1117]/10">
        <nav className="px-6 sm:px-12 lg:px-20 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" data-testid="ask-brand-logo" className="group flex items-center">
            <img src="/logo-horizontal.png" alt="Fitygrity" className="h-8 sm:h-9 w-auto object-contain rounded-md transition-opacity duration-300 group-hover:opacity-80" />
          </Link>
          <div className="flex items-center gap-3 sm:gap-4">
            <SignupLink
              testId="header-link-signup"
              className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.12em] sm:tracking-[0.2em] whitespace-nowrap text-[#FF4E00] hover:text-[#E04000] transition-colors duration-300"
            />
            <AccountMenu />
            <BackLink testId="ask-back-link" />
          </div>
        </nav>
      </header>

      <main className="px-6 sm:px-12 lg:px-20 pt-32 sm:pt-40 pb-24 sm:pb-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl mb-12"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4E00] mb-5 flex items-center gap-3">
            <Sparkles className="w-4 h-4" /> {model === "claude" ? "Powered by Claude" : "Powered by GPT"}
          </p>
          <h1 data-testid="ask-headline" className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-[#0D1117] tracking-tight leading-[1.06]">
            Ask <span className="italic text-[#FF4E00]">Fitygrity</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#374151] leading-relaxed">
            Your AI race concierge and coach — ask anything about our events, trips and gear, or
            generate a training plan built around your goal.
          </p>
        </motion.div>

        <div className="flex flex-wrap items-center gap-2 mb-10" data-testid="ask-tabs">
          {[
            { id: "concierge", label: "Race Concierge", icon: MessagesSquare },
            { id: "plan", label: "Training Plan", icon: CalendarRange },
            { id: "personal", label: "Personalised Plan", icon: UserRound },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`ask-tab-${t.id}`}
              className={`inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-full border transition-all duration-300 ${
                tab === t.id
                  ? "bg-[#FF4E00] border-[#FF4E00] text-[#FBF9F5]"
                  : "border-[#0D1117]/15 text-[#374151] hover:border-[#FF4E00]/50 hover:text-[#FF4E00]"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
          <div className="ml-auto inline-flex items-center gap-1 border border-[#0D1117]/10 rounded-full p-1 bg-[#FFFFFF]" data-testid="ai-model-toggle">
            <span className="hidden sm:inline font-mono text-[9px] uppercase tracking-[0.2em] text-[#6B7280] pl-3 pr-1">Mind</span>
            {[
              { id: "gpt", label: "GPT-5.4" },
              { id: "claude", label: "Claude Sonnet 4.6" },
            ].map((m) => (
              <button
                key={m.id}
                onClick={() => setModel(m.id)}
                data-testid={`ai-model-${m.id}`}
                className={`font-mono text-[10px] uppercase tracking-[0.12em] px-4 py-2 rounded-full transition-all duration-300 ${
                  model === m.id ? "bg-[#0D1117] text-[#FBF9F5]" : "text-[#374151] hover:text-[#FF4E00]"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {tab === "concierge" && <Concierge model={model} />}
        {tab === "plan" && (
          <PlanGenerator
            model={model}
            onRequestPersonal={() => {
              setPersonalGoal("");
              setTab("personal");
            }}
          />
        )}
        {tab === "personal" && <PersonalPlan initialGoal={personalGoal} />}
      </main>

      <Footer />
    </div>
  );
}
